import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type TouchEvent,
} from 'react';

const DRAG_THRESHOLD = 5;
const SCROLL_EDGE_THRESHOLD = 2;

export const usePathScreensScroll = (itemCount: number) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isDraggingRef = useRef(false);
  const wasDraggedRef = useRef(false);
  const dragStartXRef = useRef(0);
  const scrollStartLeftRef = useRef(0);

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const getCurrentIndex = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return 0;

    const scrollLeft = container.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (let index = 0; index < itemCount; index += 1) {
      const item = itemRefs.current[index];
      if (!item) continue;

      const distance = Math.abs(item.offsetLeft - scrollLeft);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    }

    return closestIndex;
  }, [itemCount]);

  const updateScrollState = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollLeft, clientWidth, scrollWidth } = container;
    const maxScrollLeft = scrollWidth - clientWidth;

    if (maxScrollLeft <= SCROLL_EDGE_THRESHOLD) {
      setCanScrollPrev(false);
      setCanScrollNext(false);

      return;
    }

    const currentIndex = getCurrentIndex();

    setCanScrollPrev(currentIndex > 0);
    setCanScrollNext(scrollLeft < maxScrollLeft - SCROLL_EDGE_THRESHOLD);
  }, [getCurrentIndex]);

  const getItemRef = useCallback(
    (index: number) => (element: HTMLDivElement | null) => {
      itemRefs.current[index] = element;

      if (element) {
        requestAnimationFrame(() => updateScrollState());
      }
    },
    [updateScrollState]
  );

  const scrollToIndex = useCallback((index: number) => {
    itemRefs.current[index]?.scrollIntoView({
      behavior: 'smooth',
      inline: 'start',
      block: 'nearest',
    });
  }, []);

  const scrollPrev = useCallback(() => {
    const currentIndex = getCurrentIndex();

    if (currentIndex > 0) {
      scrollToIndex(currentIndex - 1);
    }
  }, [getCurrentIndex, scrollToIndex]);

  const scrollNext = useCallback(() => {
    const currentIndex = getCurrentIndex();

    if (currentIndex < itemCount - 1) {
      scrollToIndex(currentIndex + 1);
    }
  }, [getCurrentIndex, itemCount, scrollToIndex]);

  const endDrag = useCallback(() => {
    isDraggingRef.current = false;
    const container = scrollRef.current;

    if (container) {
      container.style.cursor = 'grab';
    }

    updateScrollState();
  }, [updateScrollState]);

  const applyDragScroll = useCallback(
    (pageX: number) => {
      const container = scrollRef.current;
      if (!container || !isDraggingRef.current) return;

      const deltaX = pageX - dragStartXRef.current;

      if (Math.abs(deltaX) > DRAG_THRESHOLD) {
        wasDraggedRef.current = true;
      }

      container.scrollLeft = scrollStartLeftRef.current - deltaX;
      updateScrollState();
    },
    [updateScrollState]
  );

  const handleMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const container = scrollRef.current;
      if (!container) return;

      event.preventDefault();
      isDraggingRef.current = true;
      wasDraggedRef.current = false;
      dragStartXRef.current = event.pageX;
      scrollStartLeftRef.current = container.scrollLeft;
      container.style.cursor = 'grabbing';
    },
    []
  );

  const handleMouseMove = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      applyDragScroll(event.pageX);
    },
    [applyDragScroll]
  );

  const handleTouchStart = useCallback((event: TouchEvent<HTMLDivElement>) => {
    const container = scrollRef.current;
    if (!container) return;

    isDraggingRef.current = true;
    wasDraggedRef.current = false;
    dragStartXRef.current = event.touches[0].pageX;
    scrollStartLeftRef.current = container.scrollLeft;
  }, []);

  const handleTouchMove = useCallback(
    (event: TouchEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current) return;

      event.preventDefault();
      applyDragScroll(event.touches[0].pageX);
    },
    [applyDragScroll]
  );

  const shouldIgnoreClick = useCallback(() => {
    const wasDragged = wasDraggedRef.current;
    wasDraggedRef.current = false;

    return wasDragged;
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const frame = requestAnimationFrame(() => updateScrollState());
    container.addEventListener('scroll', updateScrollState);

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(container);

    return () => {
      cancelAnimationFrame(frame);
      container.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [itemCount, updateScrollState]);

  useEffect(() => {
    const handleWindowMouseMove = (event: globalThis.MouseEvent) => {
      applyDragScroll(event.pageX);
    };

    const handleWindowMouseUp = () => endDrag();
    const handleWindowTouchEnd = () => endDrag();

    window.addEventListener('mousemove', handleWindowMouseMove);
    window.addEventListener('mouseup', handleWindowMouseUp);
    window.addEventListener('touchend', handleWindowTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
      window.removeEventListener('touchend', handleWindowTouchEnd);
    };
  }, [applyDragScroll, endDrag]);

  return {
    scrollRef,
    getItemRef,
    scrollPrev,
    scrollNext,
    canScrollPrev,
    canScrollNext,
    shouldIgnoreClick,
    scrollHandlers: {
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: endDrag,
      onMouseLeave: endDrag,
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: endDrag,
    },
  };
};
